
import { Fragment, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Button, Flex, Tooltip, Typography, Tag, notification } from "antd";
import { ColumnsType } from "antd/es/table";

import { CurrencySelect } from "@/components/Selector";
import { AcceptStateEnum, PurchasefFnishStateEnum, PurchaseOrderItem, PurchaseOrderStateEnum } from "@/types/purchase";
import SText from "@/styles/SText";

import iconFolder from "@/assets/images/fileIcon/folder.svg";
import { dateFormat, priceFormat } from "@/utils/format";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { DeleteOutlined, DownloadOutlined, InfoCircleOutlined, RedoOutlined } from "@ant-design/icons";
import { downloadFile } from "@/components/SDoc/utils";
import { PageId } from "@/types/page";
import usePage from "@/layout/hooks/usePage";
import { divided } from "@/utils/calc";
import { numFormmater } from "@/pages/Requirement/util";
import { hasPermission } from "@/utils/user";
import { User, UserPermissionEnum } from "@/types/user";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { OrderItemV2 } from "@/types/inventoryV2";
import { ConfirmedEnum } from "@/types/inventory";
import WmsTags from "@/components/WmsTags";
import { ItemLabelEnum } from "@/types/common";

type UseMaterialTrackingColumn = (props: {
  onDetail: (params: PurchaseOrderItem | null) => void;
  onDel: (id: string) => void;
  onOpenTab: (type: 'doc', item: PurchaseOrderItem) => void;
  onIn: (record: PurchaseOrderItem) => void;
  setShowDetail: (params: PurchaseOrderItem | null) => void;
  setPreOrder: (record: PurchaseOrderItem) => void;
  onAccept: (oldItem: PurchaseOrderItem, orderId: string) => Promise<void>;
  onReject: (oldItem: PurchaseOrderItem, orderId: string) => Promise<void>;
  onRePrint: (orderId: string, rowId: string) => Promise<void>;
  onCompletedProduction: (orderId: string, val: PurchaseOrderStateEnum) => Promise<void>;
  onOpenRequirementDetail: (requirement: any) => void;
}) => ColumnsType<PurchaseOrderItem>

// 勾选只改变 selection 列；业务行对象没变时跳过业务单元格渲染。
const shouldPurchaseOrderCellUpdate = (record: PurchaseOrderItem, prevRecord: PurchaseOrderItem) => record !== prevRecord;

const withPurchaseOrderCellUpdate = (columns: ColumnsType<PurchaseOrderItem>): ColumnsType<PurchaseOrderItem> => {
  return columns.map(column => {
    const nextColumn = {
      ...column,
      shouldCellUpdate: column.shouldCellUpdate ?? shouldPurchaseOrderCellUpdate,
    };

    if ("children" in column && column.children) {
      return {
        ...nextColumn,
        children: withPurchaseOrderCellUpdate(column.children as ColumnsType<PurchaseOrderItem>),
      };
    }

    return nextColumn;
  });
};

type PurchaseOrderColumn = ColumnsType<PurchaseOrderItem>[number];

const getColumnKey = (column: PurchaseOrderColumn) => {
  if (column.key !== undefined) return String(column.key);
  if (!("dataIndex" in column) || column.dataIndex === undefined) return "";

  return Array.isArray(column.dataIndex)
    ? column.dataIndex.join(".")
    : String(column.dataIndex);
};

const reorderMaterialTrackingColumns = (
  columns: ColumnsType<PurchaseOrderItem>
): ColumnsType<PurchaseOrderItem> => {
  const columnsByKey = new Map(columns.map(column => [getColumnKey(column), column]));
  const productInfoColumn = columnsByKey.get("productInfo");
  const productInfoChildren = productInfoColumn && "children" in productInfoColumn
    ? productInfoColumn.children ?? []
    : [];

  const productMainInfoColumn = productInfoColumn
    ? {
        ...productInfoColumn,
        key: "materialTrackingProductMainInfo",
        children: productInfoChildren.slice(0, 3),
      }
    : undefined;

  const productExtraInfoColumn = productInfoColumn && productInfoChildren.length > 3
    ? {
        ...productInfoColumn,
        key: "materialTrackingProductExtraInfo",
        children: productInfoChildren.slice(3),
      }
    : undefined;

  const leadingKeys = [
    "tags",
    "supplierInfo",
    "count",
    "deliveredQuantity",
    "returnCount",
    "deliveryDeadline",
    "orderTime",
  ];
  const trailingKeys = ["relatedDocuments", "acceptState", "finishState", "orderState", "operation"];
  const movedKeys = new Set([...leadingKeys, ...trailingKeys, "productInfo"]);

  const leadingColumns = [
    columnsByKey.get("tags"),
    columnsByKey.get("supplierInfo"),
    productMainInfoColumn,
    columnsByKey.get("count"),
    columnsByKey.get("deliveredQuantity"),
    columnsByKey.get("returnCount"),
    columnsByKey.get("deliveryDeadline"),
    columnsByKey.get("orderTime"),
  ]
    .filter((column): column is PurchaseOrderColumn => Boolean(column));

  const remainingColumns = columns
    .filter(column => !movedKeys.has(getColumnKey(column)))
    .concat(productExtraInfoColumn ? [productExtraInfoColumn] : []);

  const trailingColumns = trailingKeys
    .map(key => columnsByKey.get(key))
    .filter((column): column is PurchaseOrderColumn => Boolean(column));

  return [...leadingColumns, ...remainingColumns, ...trailingColumns];
};

const useMaterialTrackingColumn: UseMaterialTrackingColumn = ({ onDetail, onDel, onOpenTab, onIn, setShowDetail, onAccept, onReject, setPreOrder, onRePrint, onCompletedProduction, onOpenRequirementDetail }) => {
  const { t } = useTranslation();
  const { pageId } = usePage();
  const currentUser: User = useSelector((state: RootState) => state.userReducer.curUser);

  // 列配置保持稳定，点击事件从 ref 读取最新回调，避免父组件重渲染时整套 columns 重建。
  const handlersRef = useRef({
    onAccept,
    onCompletedProduction,
    onDel,
    onDetail,
    onOpenRequirementDetail,
    onOpenTab,
    onReject,
    onRePrint,
    setPreOrder,
    setShowDetail,
  });

  handlersRef.current = {
    onAccept,
    onCompletedProduction,
    onDel,
    onDetail,
    onOpenRequirementDetail,
    onOpenTab,
    onReject,
    onRePrint,
    setPreOrder,
    setShowDetail,
  };

  // 齐料追踪的列顺序在此数组内独立维护，调整时不会影响其他采购订单页面。
  const columns: ColumnsType<PurchaseOrderItem> = useMemo(() => reorderMaterialTrackingColumns(withPurchaseOrderCellUpdate([
    {
      title: <SText text={t("purchase.orderNumber")} />,
      dataIndex: "number",
      width: 200,
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => {
        const isZfPage = pageId === PageId.PUR_ZF;
        return (
          <Typography.Text
            ellipsis={{ tooltip: item?.number ?? "" }}
            style={{
              color: isZfPage ? undefined : '#1890ff',
              cursor: isZfPage ? 'default' : 'pointer',
              width: '100%'
            }}
            onClick={() => !isZfPage && handlersRef.current.onDetail(item)}
          >
            {item?.number ?? ""}
          </Typography.Text>
        );
      },
    },
    {
      title: <SText text={t("sale.requirement")} />,
      dataIndex: ["quotationRequest", "requirementItem", "requirement", "number"],
      width: 200,
      sorter: (a, b) => {
        const valA = a?.quotationRequest?.requirementItem?.requirement?.number ?? "";
        const valB = b?.quotationRequest?.requirementItem?.requirement?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => {
        const requirementNumber = item?.quotationRequest?.requirementItem?.requirement?.number;
        const requirement = item?.quotationRequest?.requirementItem?.requirement;

        if (!item?.quotationRequest) {
          return <Typography.Text type="secondary">{t("sale.notRequirement")}</Typography.Text>;
        }

        if (!item.quotationRequest.requirementItem) {
          return <Typography.Text type="secondary">{t("sale.notRequirement")}</Typography.Text>;
        }

        if (!requirement) {
          return <Typography.Text type="secondary">{t("sale.notRequirement")}</Typography.Text>;
        }

        return (
          <Typography.Link
            onClick={() => handlersRef.current.onOpenRequirementDetail(requirement)}
          >
            {requirementNumber}
          </Typography.Link>
        );
      },
    },
    {
      title: t("purchase.supplierInfo"),
      key: "supplierInfo",
      // fixed: 'left',
      children: [
        {
          title: <SText text={t("purchase.code")} />,
          dataIndex: ["order", "supplier", "code"],
          width: 80,
          // fixed: 'left',
          sorter: (a, b) => {
            const valA = a?.order?.supplier?.code ?? "";
            const valB = b?.order?.supplier?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => <SText text={item?.order?.supplier?.code} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("purchase.name")} />,
          dataIndex: "name",
          width: 180,
          // fixed: 'left',
          render: (_, item) => (
            <SText text={item?.order?.supplier?.name ?? ""} style={{ width: "100%" }} />
          ),
        }
      ],
    },
    // {
    //   title: <SText text={t("purchase.type")} />,
    //   dataIndex: "type",
    //   width: 80,
    //   render: (_, item) => (
    //     <PurchaseOrderTypeSelect value={item.type.toString()} readOnly />
    //   ),
    // },
    {
      title: <SText text={t("purchase.count")} />,
      dataIndex: "count",
      width: 80,
      sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
      render: (_, item) => (
        <SText text={numFormmater(item?.count || 0)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      key: "amount",
      width: 80,
      sorter: (a, b) => {
        const getAmount = (item: any) => {
          const taxRate = item.quotationRequest?.price?.taxRate || item.taxRate;
          if (taxRate && item.rateAmount) {
            const rate = divided(taxRate ?? '0', '100');
            return Number(divided(item.rateAmount, (1 + Number(rate)).toString()));
          }
          return 0;
        };
        return getAmount(a) - getAmount(b);
      },
      render: (_, item) => {
        const taxRate = item.quotationRequest?.price?.taxRate || item.taxRate;
        if (taxRate && item.rateAmount) {
          const rate = divided(taxRate ?? '0', '100')
          const amountPtrice = divided(item.rateAmount, (1 + Number(rate)).toString())

          return <SText text={numFormmater(Number(amountPtrice))} style={{ width: "100%" }} />
        }
        else return '-'
      },
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      dataIndex: "rateAmount",
      width: 80,
      sorter: (a, b) => Number(a?.rateAmount ?? 0) - Number(b?.rateAmount ?? 0),
      render: (_, item) => (
        <SText text={priceFormat(item?.rateAmount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.spareRate")} />,
      key: "spareRate",
      width: 100,
      hidden: pageId && ![PageId.PUR_ORDER_PAYED].includes(pageId),
      render: (_, item) => <SText text={Number(item?.spareRate ?? "") + ''} />,
    },
    {
      title: <SText text={t("purchase.spareCount")} />,
      key: "spareRate",
      width: 100,
      hidden: pageId && ![PageId.PUR_ORDER_PAYED].includes(pageId),
      render: (_, item) => <SText text={numFormmater(item.spareCount || 0)} />,
    },
    {
      title: <SText text={t("purchase.orderTime")} />,
      dataIndex: "orderTime",
      width: 100,
      render: (_, item) => item.orderTime ? dateFormat(dayjs(item.orderTime)) : '',
    },
    // {
    //   title: <SText text={t("purchase.scheduledOrderTime")} />,
    //   dataIndex: "scheduledOrderTime",
    //   width: 100,
    //   render: (_, item) => item.scheduledOrderTime ? dateFormat(dayjs(item.scheduledOrderTime)) : '',
    // },
    {
      title: <SText text={t("purchase.deliveryDeadline")} />,
      dataIndex: "deliveryDeadline",
      // hidden: pageId !== PageId.PUR_MT,
      width: 100,
      render: (_, item) => item.deliveryDeadline ? dateFormat(dayjs(item.deliveryDeadline)) : '',
    },
    {
      title: <SText text={t("purchase.deliveredQuantity")} />,
      dataIndex: "deliveredQuantity",
      width: 80,
      render: (_, item) => numFormmater(item.deliveredQuantity || 0)
    },
    {
      title: <SText text={t("purchase.returnQty")} />,
      dataIndex: "returnCount",
      width: 80,
      render: (_, item) => numFormmater(item.returnCount || 0)
    },
    {
      title: <SText text={t("purchase.actualArrivalQty")} />,
      key: "actualArrivalQty",
      width: 100,
      hidden: !pageId || pageId !== PageId.PUR_ORDER_PAYED,
      render: (_, item) => (
        <SText text={numFormmater((item.deliveredQuantity || 0) - (item.returnCount || 0))} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.undeliveredQty")} />,
      key: "undeliveredQty",
      width: 100,
      hidden: !pageId || pageId !== PageId.PUR_ORDER_PAYED,
      render: (_, item) => (
        <SText text={numFormmater(Math.max(0, (item.count || 0) - (item.deliveredQuantity || 0) + (item.returnCount || 0)))} style={{ width: "100%" }} />
      ),
    },
    // {
    //   title: <SText text={t("purchase.estimateArrivalAt")} />,
    //   dataIndex: "estimateArrivalAt",
    //   width: 100,
    //   render: (_, item) => item.estimateArrivalAt ? dateFormat(dayjs(item.estimateArrivalAt)) : '',
    // },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 100,
      render: (_, item) => <CurrencySelect value={item?.order?.currency} readOnly />
    },
    {
      title: <SText text={t("purchase.payMethod")} />,
      dataIndex: "payMethod",
      width: 120,
      render: (_, item) => <Dict key={"payMethod"} type={DictEnum.PURCHASE_PAY_METHOD} value={item?.order?.payMethod} readOnly />,
    },
    {
      title: <SText text={t("purchase.orderDesc")} />,
      dataIndex: "remark",
      width: 120,
      render: (_, item) => <SText text={item?.order?.remark ?? ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t("purchase.productDesc")} />,
      dataIndex: "remark",
      width: 120,
      render: (_, item) => <SText text={item.remark ?? ''} style={{ width: "100%" }} />
    },
    {
      title: t("purchase.productInfo"),
      key: "productInfo",
      children: [
        {
          title: <SText text={t("product.code")} />,
          dataIndex: ["product", "code"],
          width: 100,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.name")} />,
          dataIndex: "name",
          width: 100,
          render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("order.brand")} />,
          dataIndex: "model1",
          width: 120,
          render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
        },
        // {
        //   title: <SText text={t("product.brand2")} />,
        //   dataIndex: "model2",
        //   width: 120,
        //   render: (_, item) => <SText text={item?.product?.t1 ?? ''} style={{ width: "100%" }} />,
        // },
        {
          title: <SText text={t("purchase.brand")} />,
          key: "brand",
          width: 80,
          render: (_, item) => <SText text={item?.brand ?? ""} />,
        },
        {
          title: <SText text={t("purchase.mpn")} />,
          key: "mpn",
          width: 80,
          render: (_, item) => <SText text={item?.mpn ?? ""} />,
        },
        {
          title: <SText text={t("purchase.dcc")} />,
          key: "dcc",
          width: 110,
          render: (_, item) => <SText text={item?.quotationRequest?.price?.dcc ?? ""} />,
        },
        {
          title: <SText text={t("product.uom")} />,
          dataIndex: "uom",
          width: 80,
          render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("common.desc")} />,
          dataIndex: "desc",
          width: 120,
          render: (_, item) => <SText text={item?.product?.desc ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("purchase.stPrice")} />,
          key: "price",
          width: 80,
          render: (_, item) => <SText text={numFormmater(Number(item?.quotationRequest?.supplierPrice || item?.price?.price || ''))} style={{ width: "100%" }} />,
        },
      ]
    },
    {
      title: <SText text={t("warehouse.labels")} />,
      key: "tags",
      width: 160,
      render: (_, record) => <WmsTags refId={record?.quotationRequest?.requirementItemId ?? ''} refType={ItemLabelEnum.REQUIREMENT_ITEM} tags={record?.quotationRequest?.requirementItem?.tags || []} />
    },
    {
      title: <SText text={t("purchase.state")} />,
      key: "acceptState",
      dataIndex: "acceptState",
      fixed: "right",
      hidden: !pageId || (pageId && ![PageId.PUR_ORDER_PAYED, PageId.PUR_HELP_ACCEPT, PageId.PUR_MT].includes(pageId)),
      width: 100,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          {
            item.order?.acceptState === null ? (
              <Tag color="blue">{t("purchase.pend")}</Tag>
            ) : item.order?.acceptState === AcceptStateEnum.RESOLVED ? (
              <Tag color="green">{t("purchase.acceptType")}</Tag>
            ) : (
              <div>
                <Tooltip placement="topLeft" title={
                  <div>
                    <p>{t("purchase.state")}: {item?.order?.acceptState === AcceptStateEnum.REJECTED ? t("purchase.reject") : ''}</p>
                    <p>{t("purchase.rejectDate")}: {dateFormat(dayjs(item?.order?.acceptedAt))}</p>
                    <p>{t("purchase.operationPerson")}: {item?.order?.acceptor.name1 || item?.order?.acceptor.name2 || item?.order?.acceptor.mobile}</p>
                  </div>
                }>
                  <Tag color="red">{t("purchase.rejectType")}</Tag>
                </Tooltip>
                <InfoCircleOutlined hidden={pageId === PageId.PUR_HELP_ACCEPT} style={{ color: 'red', marginRight: -15 }} onClick={() => handlersRef.current.setShowDetail(item)} />
              </div>

            )
          }
        </Flex>
      ),
    },
    {
      title: <SText text={t("purchase.deliveryStatus")} />,
      key: "finishState",
      dataIndex: "finishState",
      fixed: "right",
      width: 80,
      render: (_, item) => {
        const finishState = item.finishState;
        if (finishState === PurchasefFnishStateEnum.UN_FINISHED) {
          return <Tag color="orange">{t("purchase.pendingProduction")}</Tag>;
        } else if (finishState === PurchasefFnishStateEnum.FINISHING) {
          return <Tag color="blue">{t("purchase.inProduction")}</Tag>;
        } else if (finishState === PurchasefFnishStateEnum.FINISHED) {
          return <Tag color="green">{t("purchase.finishedProduction")}</Tag>;
        }
        return '-';
      },
    },
    {
      title: <SText text={t("purchase.orderState")} />,
      key: "orderState",
      dataIndex: "orderState",
      fixed: "right",
      width: 80,
      render: (_, item) => {
        const orderState = item.orderState;
        if (orderState === PurchaseOrderStateEnum.PAY_PENDING) {
          return <Tag color="default">{t("purchase.notPurchase")}</Tag>;
        } else if (orderState === PurchaseOrderStateEnum.PAYED) {
          return <Tag color="blue">{t("purchase.purchased")}</Tag>;
        } else if (orderState === PurchaseOrderStateEnum.DONE) {
          return <Tag color="green">{t("layout.purDone")}</Tag>;
        } else if (orderState === PurchaseOrderStateEnum.COMPLETED) {
          return <Tag color="purple">{t("purchase.completedProduction")}</Tag>;
        }
        return '-';
      },
    },
    {
      //PageId.PUR_ORDER_PAYED
      title: <SText text={t("purchase.deliveryProgress")} />,
      dataIndex: "dsa",
      fixed: "right",
      hidden: !pageId || pageId !== PageId.PUR_ORDER_PAYED,
      width: 120,
      render: (_, item) => {
        let sum = item.inventoryItems ? item.inventoryItems.length : 0;
        let count = 0;
        item.inventoryItems && item.inventoryItems.forEach((i: OrderItemV2) => {
          if (i.order.confirmState === ConfirmedEnum.CONFIRM) count++
        })
        return <SText text={`${count}/${sum}`} style={{ width: "100%" }} />
      },
    },
    {
      title: <SText text={t("purchase.relatedDocuments")} />,
      dataIndex: "relatedDocuments",
      fixed: "right",
      width: 60,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => handlersRef.current.onOpenTab("doc", item)}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      width: pageId === PageId.PUR_RT ? 120 : 240,
      fixed: "right",
      hidden: (pageId && [PageId.PUR_ZF, PageId.PUR_MT].includes(pageId)) || !!!pageId,
      render: (val, record) => (
        pageId === PageId.PUR_HELP_ACCEPT ? (record.order.acceptState === null && <Fragment>
          <Button type="primary" onClick={() => {
            handlersRef.current.onAccept(record, record.order.id).catch(() => { })
          }}>{t("purchase.accept")}</Button>
          <Button type="link" danger onClick={() => {
            handlersRef.current.onReject(record, record.order.id).catch(() => { })
          }}>{t("purchase.reject")}</Button>
        </Fragment>)
          : <Flex justify="flex-start" gap={6}>
            {pageId !== PageId.PUR_RT && <Button type="primary" onClick={() => handlersRef.current.onDetail(record)}>
              {t("common.detail")}
            </Button>}
            {
              record.order?.acceptState === 1 && <Button type="primary" onClick={() => handlersRef.current.setPreOrder(record)}>{t('purchase.delivery')}</Button>
            }
            {
              record.orderState !== PurchaseOrderStateEnum.COMPLETED && pageId === PageId.PUR_ORDER_PAYED && <Button type="primary" onClick={() => {
                handlersRef.current.onCompletedProduction(record.id ?? '', PurchaseOrderStateEnum.COMPLETED)
              }}>{t('purchase.completedProduction')}</Button>
            }

            {/* <Button type="primary" onClick={() => onIn(record)}>
            {t("sale.inStock")}
          </Button> */}
            {pageId && ![PageId.PUR_ORDER_DONE, PageId.PUR_RT].includes(pageId) && hasPermission(currentUser, [UserPermissionEnum.PUR_ORDER_EDIT]) && <Tooltip title={t('purchase.del')}>
              <Button type="link" danger onClick={() => handlersRef.current.onDel(record.id)} icon={<DeleteOutlined />} />
            </Tooltip>}
            {record.printItem && (<Fragment>
              <Tooltip title={t('purchase.rePrt')}>
                <Button type="link" onClick={() => { handlersRef.current.onRePrint(record.printItem?.order.id ?? '', record.id).catch(() => { }) }} icon={<RedoOutlined />} />
              </Tooltip>
              <Tooltip title={t('purchase.downloadFile')}>
                <Button type="link" onClick={() => {
                  if (record.printItem?.order.fileId) downloadFile(record.printItem?.order.fileId)
                  else notification.error({ placement: 'top', message: t("app.downFailError", { error: t('apiError.error12') }) })
                }} icon={<DownloadOutlined />} />
              </Tooltip>
            </Fragment>
            )}
          </Flex>
      ),
    },
  ])), [t, pageId, currentUser]);

  return columns;
}

export default useMaterialTrackingColumn;
